import React from 'react';
import {
  Alert,
  Box,
  Button,
  Grid,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { EFieldConfig } from '../../../components/form';
import {createFieldObject} from '../../../components/form';
import { formatDate } from '../../../components/renderers';

import BudgetConsolidationCardsPanel from './BudgetConsolidationCardsPanel';
import BudgetConsolidationAccountGrid from './BudgetConsolidationAccountGrid';
import BudgetConsolidationCostCenterGrid from './BudgetConsolidationCostCenterGrid';

import {
  fetchBudgetConsolidationAccount,
  fetchBudgetConsolidationCards,
  fetchBudgetConsolidationCostCenter,
} from './budgetConsolidationApi';

type State = {
  cards: any;
  accountRows: any[];
  costCenterRows: any[];
  loading: boolean;
  error: string;
  success: string;
};

class BudgetConsolidationWorkbench extends React.Component<any, State> {
  state: State = {
    cards: {},
    accountRows: [],
    costCenterRows: [],
    loading: false,
    error: '',
    success: '',
  };

  componentDidMount() {
    this.load();
  }

  formconfigs: { [name: string]: EFieldConfig } = {
    bpc: {
      id: 'bpc',
      label: 'Fiscal Year',
      type: 'fc',
      req: 1,
      width: 120,
      cp: { t: 'bcs', s: 'budgetperiod' },
    },
  };

  makefield = (fieldconf: EFieldConfig): any => {
    const { service = 'budgetconsolidation', record = {} } = this.props;
    const mode = 'add';
    const formatters = { dob: formatDate };
    const onChange = this.onChange.bind(this);
    const extras = {};
    return createFieldObject(
      service,
      mode,
      fieldconf,
      record,
      formatters,
      onChange,
      extras,
    );
  };

  onChange = (name: 'fiscalYear') => (e: any) => {
    const raw = e?.target ? e.target.value : e;
    this.setState({ [name]: String(raw || '').toUpperCase() } as any);
  };

  setField = (name: 'fiscalYear' | 'stage') => (e: any) =>
    this.setState({ [name]: e.target.value } as any);

  load = async () => {
    this.setState({ loading: true, error: '', success: '' });

    try {
      const [cards, accountRows, costCenterRows] = await Promise.all([
        fetchBudgetConsolidationCards(),
        fetchBudgetConsolidationAccount(),
        fetchBudgetConsolidationCostCenter(),
      ]);

      this.setState({
        cards,
        accountRows,
        costCenterRows,
        success: 'Budget consolidation refreshed.',
      });
    } catch (err: any) {
      this.setState({ error: err.message || String(err) });
    } finally {
      this.setState({ loading: false });
    }
  };

  render() {
    const { cards, accountRows, costCenterRows, loading, error, success } =
      this.state;

    return (
      <Box sx={{ p: 3 }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent='space-between'
          alignItems={{ xs: 'stretch', md: 'center' }}
          spacing={2}
          sx={{ mb: 3 }}
        >
          <Box>
            <Typography variant='h4' sx={{ fontWeight: 700 }}>
              Budget Consolidation Workbench
            </Typography>
            <Typography variant='body2' color='text.secondary'>
              Consolidated budget view across activity, direct and investment
              budget sources.
            </Typography>
          </Box>

          <Button
            variant='contained'
            startIcon={<RefreshIcon />}
            onClick={this.load}
            disabled={loading}
          >
            Refresh
          </Button>
        </Stack>

        {error && (
          <Alert severity='error' sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}
        {success && !error && (
          <Alert severity='success' sx={{ mb: 3 }}>
            {success}
          </Alert>
        )}

        <Stack spacing={3}>
          <BudgetConsolidationCardsPanel cards={cards} />

          <Grid container spacing={3}>
            <Grid size={{ xs: 12 }}>
              <Paper elevation={1} sx={{ p: 0 }}>
                <BudgetConsolidationAccountGrid
                  rows={accountRows}
                  loading={loading}
                />
              </Paper>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Paper elevation={1} sx={{ p: 0 }}>
                <BudgetConsolidationCostCenterGrid
                  rows={costCenterRows}
                  loading={loading}
                />
              </Paper>
            </Grid>
          </Grid>
        </Stack>
      </Box>
    );
  }
}

export default BudgetConsolidationWorkbench;
